/**
 * Map CachedList-style nested filter objects to ItemProgressSearchInput for
 * the itemProgressSearch GraphQL field.
 */
function asStr(v) {
    if (v == null)
        return '';
    return String(v);
}
/**
 * Flattens `and` and top-level field keys (CustomerTracking uses flat filter).
 */
function collectClauses(filter) {
    if (!filter || typeof filter !== 'object')
        return [];
    const c = [];
    if (Array.isArray(filter.and)) {
        c.push(...filter.and);
    }
    const { and, or, not, ...rest } = filter;
    for (const k of Object.keys(rest)) {
        c.push({ [k]: rest[k] });
    }
    return c;
}
function isEqOp(op) {
    return op != null && typeof op === 'object' && 'eq' in op;
}
function isGtLtOp(op) {
    return op != null && typeof op === 'object' && ('gt' in op || 'lt' in op);
}
/**
 * @param organizationID — from current org (always wins over filter)
 */
export function buildItemProgressSearchInput(organizationID, filter, sort, limit, offset) {
    const out = {
        organizationID: organizationID || undefined,
        limit: limit > 0 ? limit : 50,
        offset: offset >= 0 ? offset : 0,
        sortField: sort?.field || 'startedDate',
        sortDirection: sort?.direction || 'asc',
    };
    const customerIDs = [];
    const courseIDs = [];
    const microcastIDs = [];
    const programIDs = [];
    const documentIDs = [];
    const certificationIDs = [];
    const itemTypes = [];
    let progressMin;
    let progressMax;
    let progressEquals;
    let startedAfter;
    let startedBefore;
    let completeAfter;
    let completeBefore;
    let lastActiveAfter;
    let lastActiveBefore;
    let startedOnly = false;
    let requireStartedDate;
    let requireCertifications;
    let hasCertification;
    let certificateStatus;
    const clauses = collectClauses(filter);
    for (const cl of clauses) {
        if (!cl || typeof cl !== 'object')
            continue;
        const c = cl;
        if (c.organizationID && isEqOp(c.organizationID)) {
            if (!out.organizationID) {
                out.organizationID = asStr(c.organizationID.eq);
            }
            continue;
        }
        if (c.or && Array.isArray(c.or)) {
            for (const o of c.or) {
                if (!o || typeof o !== 'object')
                    continue;
                const b = o;
                if (b.customerID && isEqOp(b.customerID)) {
                    customerIDs.push(asStr(b.customerID.eq));
                }
                if (b.courseID && b.courseID.matchPhrase) {
                    courseIDs.push(String(b.courseID.matchPhrase));
                }
                if (b.microcastID && b.microcastID.matchPhrase) {
                    microcastIDs.push(String(b.microcastID.matchPhrase));
                }
                if (b.programID && b.programID.matchPhrase) {
                    programIDs.push(String(b.programID.matchPhrase));
                }
                if (b.documentID && b.documentID.matchPhrase) {
                    documentIDs.push(String(b.documentID.matchPhrase));
                }
                if (b.certifications && b.certifications.matchPhrase) {
                    certificationIDs.push(String(b.certifications.matchPhrase));
                }
                if (b.certificateStatus && b.certificateStatus.matchPhrase) {
                    certificateStatus = String(b.certificateStatus.matchPhrase);
                }
                if (b.certificateStatus && b.certificateStatus.eq) {
                    certificateStatus = asStr(b.certificateStatus.eq);
                }
            }
            continue;
        }
        if (c.customerID && isEqOp(c.customerID)) {
            customerIDs.push(asStr(c.customerID.eq));
        }
        if (c.courseID && c.courseID.matchPhrase) {
            courseIDs.push(String(c.courseID.matchPhrase));
        }
        if (c.progress && isEqOp(c.progress)) {
            const peq = c.progress.eq;
            if (peq != null)
                progressEquals = Number(peq);
        }
        else if (c.progress && isGtLtOp(c.progress) && c.progress) {
            const p = c.progress;
            if (p.gt != null)
                progressMin = p.gt;
            if (p.lt != null)
                progressMax = p.lt;
        }
        if (c.startedDate) {
            const s = c.startedDate;
            if (isGtLtOp(s) && s) {
                if (s.gt)
                    startedAfter = asStr(s.gt);
                if (s.lt)
                    startedBefore = asStr(s.lt);
            }
            else if (s.ne !== undefined) {
                if (asStr(s.ne) === '')
                    startedOnly = true;
            }
            else if (s && 'exists' in s && s.exists) {
                requireStartedDate = true;
            }
        }
        if (c.completeDate && isGtLtOp(c.completeDate) && c.completeDate) {
            const d = c.completeDate;
            if (d.gt)
                completeAfter = asStr(d.gt);
            if (d.lt)
                completeBefore = asStr(d.lt);
        }
        if (c.lastActiveDate && isGtLtOp(c.lastActiveDate) && c.lastActiveDate) {
            const d = c.lastActiveDate;
            if (d.gt)
                lastActiveAfter = asStr(d.gt);
            if (d.lt)
                lastActiveBefore = asStr(d.lt);
        }
        if (c.hasCertification && isEqOp(c.hasCertification) && c.hasCertification) {
            hasCertification = Boolean(c.hasCertification.eq);
        }
        if (c.certifications) {
            const x = c.certifications;
            if (x && x.exists)
                requireCertifications = true;
        }
        const withExists = (k, t) => {
            const o = c[k];
            if (o && o.exists)
                itemTypes.push(t);
        };
        withExists('courseID', 'course');
        withExists('microcastID', 'microcast');
        withExists('programID', 'program');
        withExists('documentID', 'document');
    }
    if (customerIDs.length > 0)
        out.customerIDs = [...new Set(customerIDs)];
    if (courseIDs.length > 0)
        out.courseIDs = [...new Set(courseIDs)];
    if (microcastIDs.length > 0)
        out.microcastIDs = [...new Set(microcastIDs)];
    if (programIDs.length > 0)
        out.programIDs = [...new Set(programIDs)];
    if (documentIDs.length > 0)
        out.documentIDs = [...new Set(documentIDs)];
    if (certificationIDs.length > 0)
        out.certificationIDs = [...new Set(certificationIDs)];
    if (itemTypes.length > 0)
        out.itemTypes = [...new Set(itemTypes)];
    if (progressMin != null)
        out.progressMin = progressMin;
    if (progressMax != null)
        out.progressMax = progressMax;
    if (progressEquals != null)
        out.progressEquals = progressEquals;
    if (startedAfter)
        out.startedAfter = startedAfter;
    if (startedBefore)
        out.startedBefore = startedBefore;
    if (completeAfter)
        out.completeAfter = completeAfter;
    if (completeBefore)
        out.completeBefore = completeBefore;
    if (lastActiveAfter)
        out.lastActiveAfter = lastActiveAfter;
    if (lastActiveBefore)
        out.lastActiveBefore = lastActiveBefore;
    if (startedOnly)
        out.startedOnly = true;
    if (requireStartedDate)
        out.requireStartedDate = true;
    if (requireCertifications)
        out.requireCertifications = true;
    if (hasCertification != null)
        out.hasCertification = hasCertification;
    if (certificateStatus)
        out.certificateStatus = certificateStatus;
    out.sortField = out.sortField || 'startedDate';
    out.organizationID = organizationID;
    return out;
}
//# sourceMappingURL=itemProgressSearchInput.js.map